// app/javascript/controllers/naics_filter_controller.js
//
// Manages a modal-based NAICS classification search-and-select picker for
// input assignment rows. Selected codes are stored as comma-separated values
// in a hidden input and displayed as removable tags.
//
// The modal is moved to document.body when opened so that CSS fixed
// positioning always resolves against the viewport — even when the
// controller element lives inside overflow/transform ancestors (e.g.
// scrollable tables with SortableJS).
import {Controller} from "stimulus";

export default class extends Controller {
    static get targets() {
        return ["hidden", "tags", "modal", "searchInput", "searchResults"];
    }

    connect() {
        // Cache references to modal internals — they move out of the
        // controller DOM tree on open, so Stimulus targets won't
        // resolve until the modal is moved back.
        this._modal = this.modalTarget;
        this._searchInput = this.searchInputTarget;
        this._searchResults = this.searchResultsTarget;
        this._updateDisplay();
    }

    disconnect() {
        if (this._modal && this._modal.parentNode === document.body) {
            this._teardownModalListeners();
            document.body.removeChild(this._modal);
        }
    }

    openModal(event) {
        event.preventDefault();
        this._openerElement = event.currentTarget;
        this._dirty = false;
        document.body.appendChild(this._modal);
        this._setupModalListeners();
        this._modal.classList.remove("hidden");
        this._searchInput.value = "";
        this._searchResults.innerHTML = "";
        setTimeout(() => { this._searchInput.focus(); }, 100);
    }

    closeModal(event) {
        if (event) event.preventDefault();
        this._modal.classList.add("hidden");
        this._teardownModalListeners();
        this.element.appendChild(this._modal);
        if (this._dirty) {
            this._triggerSave();
            this._dirty = false;
        }
        if (this._openerElement) {
            this._openerElement.focus();
            this._openerElement = null;
        }
    }

    search() {
        clearTimeout(this._searchTimer);
        const query = this._searchInput.value.trim();

        if (query.length === 0) {
            this._searchResults.innerHTML = "";
            return;
        }

        this._searchTimer = setTimeout(() => {
            fetch("/classifications/query.json?search=" + encodeURIComponent(query) + "&classification_type=NAICS")
                .then(response => response.json())
                .then(data => { this._renderResults(data); })
                .catch(error => {
                    console.warn("NAICS search error:", error);
                    this._searchResults.innerHTML =
                        '<p class="text-sm text-gray-500 p-3">Search failed. Please try again.</p>';
                });
        }, 400);
    }

    selectCode(event) {
        event.preventDefault();
        const code = event.currentTarget.dataset.naicsCode;
        const name = event.currentTarget.dataset.naicsName;
        if (!code) return;

        const codes = this._currentCodes();
        if (codes.indexOf(code) !== -1) return;

        codes.push(code);
        this.hiddenTarget.value = codes.join(",");
        this._storeLabel(code, name);
        this._updateDisplay();
        this._dirty = true;

        const check = event.currentTarget.querySelector(".naics-check");
        if (check) check.classList.remove("hidden");
        event.currentTarget.classList.add("bg-purple-50");
    }

    remove(event) {
        event.preventDefault();
        const code = event.currentTarget.dataset.naicsFilterCode;
        const codes = this._currentCodes().filter(c => c !== code);
        this.hiddenTarget.value = codes.join(",");
        this._updateDisplay();
        this._triggerSave();
    }

    // ── Private: modal lifecycle ────────────────────────────────

    _setupModalListeners() {
        this._onClose = (e) => { this.closeModal(e); };
        this._onSearchInput = () => { this.search(); };
        this._onResultsClick = (e) => {
            const btn = e.target.closest("[data-naics-code]");
            if (btn) {
                e.preventDefault();
                this.selectCode({preventDefault() {}, currentTarget: btn});
            }
        };
        this._onKeydown = (e) => {
            if (e.key === "Escape") this.closeModal(e);
        };

        this._modal.querySelector("[data-naics-backdrop]")
            .addEventListener("click", this._onClose);
        this._modal.querySelector("[data-naics-close]")
            .addEventListener("click", this._onClose);
        this._modal.querySelector("[data-naics-done]")
            .addEventListener("click", this._onClose);
        this._searchInput.addEventListener("input", this._onSearchInput);
        this._searchResults.addEventListener("click", this._onResultsClick);
        document.addEventListener("keydown", this._onKeydown);
    }

    _teardownModalListeners() {
        var el;

        el = this._modal.querySelector("[data-naics-backdrop]");
        if (el && this._onClose) el.removeEventListener("click", this._onClose);

        el = this._modal.querySelector("[data-naics-close]");
        if (el && this._onClose) el.removeEventListener("click", this._onClose);

        el = this._modal.querySelector("[data-naics-done]");
        if (el && this._onClose) el.removeEventListener("click", this._onClose);

        if (this._onSearchInput)
            this._searchInput.removeEventListener("input", this._onSearchInput);

        if (this._onResultsClick)
            this._searchResults.removeEventListener("click", this._onResultsClick);

        if (this._onKeydown)
            document.removeEventListener("keydown", this._onKeydown);
    }

    // ── Private: data helpers ───────────────────────────────────

    _currentCodes() {
        var val = this.hiddenTarget.value;
        if (!val || val.trim() === "") return [];
        return val.split(",").map(function(s) { return s.trim(); }).filter(function(s) { return s !== ""; });
    }

    _storeLabel(code, name) {
        var labels = this._getLabels();
        labels[code] = name;
        this.element.dataset.naicsLabels = JSON.stringify(labels);
    }

    _getLabels() {
        try {
            return JSON.parse(this.element.dataset.naicsLabels || "{}");
        } catch (e) {
            return {};
        }
    }

    // ── Private: rendering ──────────────────────────────────────

    _renderResults(classifications) {
        var container = this._searchResults;
        var currentCodes = this._currentCodes();
        container.innerHTML = "";

        if (classifications.length === 0) {
            container.innerHTML = '<p class="text-sm text-gray-500 p-3">No classifications found.</p>';
            return;
        }

        classifications.forEach(function(cls) {
            var isSelected = currentCodes.indexOf(cls.code) !== -1;

            var item = document.createElement("button");
            item.type = "button";
            // m-0: application.scss's bare `button { margin: 2rem }` is
            // imported after Tailwind's preflight and wins over it, so every
            // button this controller builds has to name its own margin. Without
            // it the result rows sit 4rem apart, indented past their own list.
            item.className =
                "m-0 w-full text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 flex items-center justify-between border-b border-gray-100" +
                (isSelected ? " bg-purple-50" : "");
            item.dataset.naicsCode = cls.code;
            item.dataset.naicsName = cls.name;

            var label = document.createElement("span");
            label.className = "truncate mr-2";
            label.textContent = cls.code + ": " + cls.name;
            item.appendChild(label);

            var check = document.createElement("span");
            check.className = "naics-check text-purple-600 font-bold flex-shrink-0" +
                (isSelected ? "" : " hidden");
            check.textContent = "\u2713";
            item.appendChild(check);

            container.appendChild(item);
        });
    }

    _updateDisplay() {
        var codes = this._currentCodes();
        var container = this.tagsTarget;
        var labels = this._getLabels();
        container.innerHTML = "";

        codes.forEach(function(code) {
            var name = labels[code];
            var displayText = name ? code + ": " + name : code;

            // The chip carries no margin of its own: its container is a
            // flex-wrap row with gap-1, so a margin here would stack on top of
            // the gap. Trailing-icon padding is asymmetric -- the right side
            // matches the vertical padding, because the button supplies its own
            // visual weight where a second 6px would read as a gap.
            var tag = document.createElement("span");
            tag.className =
                "inline-flex items-center gap-1 rounded bg-purple-100 py-0.5 pr-0.5 pl-2 text-xs font-medium text-purple-800";
            // A chip is addressable as a chip. Tests that reach for it by tag
            // name instead end up counting whatever spans the button happens
            // to contain, and then break -- or worse, quietly start asserting
            // about the button's insides -- the next time this markup changes.
            tag.dataset.naicsChip = code;

            var text = document.createTextNode(displayText);
            tag.appendChild(text);

            // m-0, p-0 and the explicit h-4/w-4 box are load-bearing, not
            // tidiness: application.scss ends with a bare `button { margin:
            // 2rem; padding: .55em 1em; display: block; font-size: 1.25em }`
            // imported after Tailwind's preflight, so a button that names none
            // of those inherits all of them and inflates this chip to roughly
            // five times its height.
            var btn = document.createElement("button");
            btn.type = "button";
            btn.className =
                "relative m-0 inline-flex h-4 w-4 items-center justify-center rounded-sm p-0 " +
                "text-xs font-bold leading-none " +
                // ring-2 matches the Add/Edit trigger, the modal close and Done
                // in _naics_filter_cell.html.erb. No ring-offset: the offset is
                // painted white, which on the chip's bg-purple-100 would read as
                // a halo rather than the breathing room it gives on white.
                "text-purple-500 hover:text-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500";
            btn.textContent = "\u00d7";
            btn.setAttribute("aria-label", "Remove NAICS filter " + displayText);

            // The glyph's own box is 16px, which keeps the chip chip-sized but
            // leaves less to hit than a pointer target should offer. This
            // overlay grows what a click lands on without growing what the eye
            // sees; appended after textContent, which would otherwise erase it.
            var hitArea = document.createElement("span");
            hitArea.className = "absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2";
            hitArea.setAttribute("aria-hidden", "true");
            btn.appendChild(hitArea);
            btn.dataset.action = "click->naics-filter#remove";
            btn.dataset.naicsFilterCode = code;
            tag.appendChild(btn);

            container.appendChild(tag);
        });
    }

    _triggerSave() {
        this.hiddenTarget.dispatchEvent(new Event("change", {bubbles: true}));
    }
}
